import { Card, Empty, Progress, Skeleton, Space, Typography, Row, Col, Tag, Badge, Tooltip, Statistic } from "antd";
import { Component, IEnvDetails } from "../../types";
import { useEffect, useState, useMemo } from "react";
import { getKubeService } from "../../backend";
import { useParams } from "react-router";
import { GetKubernetesStatusResponse, ListKubernetesWithDetailsResponseNodesStatusNodes } from "@microtica/ms-kube-sdk";
import { AlertTriangle, Check, CheckCircle, Copy, Database, Info, Lock, Server, ShieldCheck, Zap, Activity } from "lucide-react";
import KubernetesLogo from "../../assets/kubernetes-icon.svg";

const { Text, Paragraph } = Typography;

interface KubernetesOverviewProps {
    env: IEnvDetails;
    component: Component;
}

// Sub-components for better organization
interface ClusterHeaderProps {
    clusterId: string;
    version?: string;
    associatedNamespace?: string;
    nodes: ListKubernetesWithDetailsResponseNodesStatusNodes[];
}

const ClusterHeader = ({ clusterId, version, associatedNamespace, nodes }: ClusterHeaderProps) => {
    const stats = useMemo(() => {
        const healthyNodes = nodes.filter(n => n.resources.status === "OK").length;
        const totalCpu = nodes.reduce((sum, n) => sum + (n.resources.allocated.cpu / 1000), 0);
        const usedCpu = nodes.reduce((sum, n) => sum + (n.resources.usage.cpu / 1000), 0);
        const totalMemory = nodes.reduce((sum, n) => sum + (n.resources.allocated.memory / 1024), 0);
        const usedMemory = nodes.reduce((sum, n) => sum + (n.resources.usage.memory / 1024), 0);
        
        return {
            totalNodes: nodes.length,
            healthyNodes,
            avgCpuUsage: nodes.length > 0 ? Math.round((usedCpu / totalCpu) * 100) : 0,
            avgMemoryUsage: nodes.length > 0 ? Math.round((usedMemory / totalMemory) * 100) : 0,
        };
    }, [nodes]);

    const getHealthStatus = () => {
        if (stats.healthyNodes === stats.totalNodes && stats.totalNodes > 0) return 'success';
        if (stats.healthyNodes > 0) return 'warning';
        return 'error';
    };

    const healthStatus = getHealthStatus();

    return (
        <Card
            bordered
            style={{
                borderRadius: '6px',
                marginBottom: '8px',
                border: '1px solid #e8e8e8',
                background: '#ffffff'
            }}
            bodyStyle={{ padding: '12px' }}
        >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '10px' }}>
                <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '6px',
                    background: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #e8e8e8',
                    flexShrink: 0
                }}>
                    <img src={KubernetesLogo} style={{ width: "20px", height: "20px" }} alt="kubernetes" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: '2px', color: '#262626' }}>
                        Kubernetes Cluster
                    </Text>
                    <Paragraph 
                        copyable={{ 
                            text: clusterId,
                            icon: [<Copy size={11} key="copy" />, <Check size={11} key="copied" />]
                        }}
                        style={{ 
                            margin: 0,
                            fontSize: '11px',
                            fontWeight: 500,
                            fontFamily: 'monospace',
                            color: '#595959'
                        }}
                    >
                        {clusterId}
                    </Paragraph>
                </div>
            </div>
            
            {/* Combined stats and metadata */}
            <div style={{ 
                display: 'flex', 
                gap: '8px', 
                flexWrap: 'wrap',
                alignItems: 'center',
                paddingTop: '10px',
                borderTop: '1px solid #f0f0f0'
            }}>
                {version && (
                    <div style={{
                        padding: '3px 8px',
                        background: '#fafafa',
                        borderRadius: '4px',
                        border: '1px solid #e8e8e8',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}>
                        <Info size={11} style={{ color: '#8c8c8c' }} />
                        <Text style={{ color: '#595959', fontSize: '11px' }}>{version}</Text>
                    </div>
                )}
                <div style={{
                    padding: '3px 8px',
                    background: '#fafafa',
                    borderRadius: '4px',
                    border: '1px solid #e8e8e8',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                }}>
                    {associatedNamespace ? (
                        <Lock size={11} style={{ color: '#8c8c8c' }} />
                    ) : (
                        <ShieldCheck size={11} style={{ color: '#8c8c8c' }} />
                    )}
                    <Text style={{ color: '#595959', fontSize: '11px' }}>
                        {associatedNamespace ? `${associatedNamespace}` : 'All namespaces'}
                    </Text>
                </div>
                
                {/* Stats inline */}
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginLeft: 'auto' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Server size={12} style={{ color: '#595959' }} />
                        <Text style={{ fontSize: '11px', color: '#262626', fontWeight: 600 }}>{stats.totalNodes}</Text>
                        <Text style={{ fontSize: '11px', color: '#8c8c8c', marginLeft: '2px' }}>nodes</Text>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <CheckCircle size={12} style={{ 
                            color: healthStatus === 'success' ? '#52c41a' : healthStatus === 'warning' ? '#faad14' : '#ff4d4f'
                        }} />
                        <Text style={{ 
                            fontSize: '11px', 
                            color: healthStatus === 'success' ? '#52c41a' : healthStatus === 'warning' ? '#faad14' : '#ff4d4f',
                            fontWeight: 600
                        }}>{stats.healthyNodes}</Text>
                        <Text style={{ fontSize: '11px', color: '#8c8c8c', marginLeft: '2px' }}>healthy</Text>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Activity size={12} style={{ color: '#595959' }} />
                        <Text style={{ fontSize: '11px', color: '#262626', fontWeight: 600 }}>{stats.avgCpuUsage}%</Text>
                        <Text style={{ fontSize: '11px', color: '#8c8c8c', marginLeft: '2px' }}>CPU</Text>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Database size={12} style={{ color: '#595959' }} />
                        <Text style={{ fontSize: '11px', color: '#262626', fontWeight: 600 }}>{stats.avgMemoryUsage}%</Text>
                        <Text style={{ fontSize: '11px', color: '#8c8c8c', marginLeft: '2px' }}>memory</Text>
                    </div>
                </div>
            </div>
        </Card>
    );
};

interface NodeCardProps {
    node: ListKubernetesWithDetailsResponseNodesStatusNodes;
    index: number;
    isMobile: boolean;
    isLast: boolean;
}

const NodeCard = ({ node, index, isMobile, isLast }: NodeCardProps) => {
    const isHealthy = node.resources.status === "OK";
    const cpuUsagePercent = isHealthy 
        ? Math.round((node.resources.usage.cpu / node.resources.allocated.cpu) * 100)
        : 0;
    const memoryUsagePercent = isHealthy
        ? Math.round((node.resources.usage.memory / node.resources.allocated.memory) * 100)
        : 0;
    const cpuUsed = (node.resources.usage.cpu / 1000).toFixed(1);
    const cpuAllocated = (node.resources.allocated.cpu / 1000).toFixed(1);
    const memoryUsed = (node.resources.usage.memory / 1024).toFixed(1);
    const memoryAllocated = (node.resources.allocated.memory / 1024).toFixed(1);
    const cpuAvailable = (parseFloat(cpuAllocated) - parseFloat(cpuUsed)).toFixed(1);
    const memoryAvailable = (parseFloat(memoryAllocated) - parseFloat(memoryUsed)).toFixed(1);
    const isHighUsage = cpuUsagePercent > 80 || memoryUsagePercent > 80;
    const overallHealth = Math.round((cpuUsagePercent + memoryUsagePercent) / 2);

    const getHealthColor = () => {
        if (overallHealth > 80) return '#ff4d4f';
        if (overallHealth > 60) return '#fa8c16';
        return '#52c41a';
    };

    return (
        <Card
            bordered
            style={{
                borderRadius: '6px',
                border: isHighUsage ? `2px solid ${getHealthColor()}` : '1px solid #e8e8e8',
                background: '#ffffff',
                marginBottom: isLast ? 0 : '10px',
                transition: 'all 0.2s ease'
            }}
            bodyStyle={{ padding: isMobile ? '8px' : '10px' }}
        >
            {/* Node Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
            }}>
                <div style={{
                    width: isMobile ? '28px' : '32px',
                    height: isMobile ? '28px' : '32px',
                    borderRadius: '6px',
                    background: isHealthy ? '#f0f7ff' : '#fff1f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1px solid ${isHealthy ? '#d6e4ff' : '#ffccc7'}`,
                    flexShrink: 0
                }}>
                    <Server size={isMobile ? 12 : 14} style={{ color: isHealthy ? '#1890ff' : '#ff4d4f' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <Text 
                        strong 
                        style={{ 
                            fontSize: isMobile ? '12px' : '13px',
                            display: 'block',
                            wordBreak: 'break-word',
                            lineHeight: '1.2',
                            marginBottom: '1px',
                            color: '#262626'
                        }}
                    >
                        {node.name}
                    </Text>
                    {node.version && (
                        <Text type="secondary" style={{ fontSize: '11px', display: 'block', color: '#8c8c8c' }}>
                            {node.version}
                        </Text>
                    )}
                </div>
                {isHighUsage && (
                    <Tooltip title="High resource usage">
                        <AlertTriangle size={12} style={{ color: '#ff4d4f', flexShrink: 0 }} />
                    </Tooltip>
                )}
                {!isHealthy && (
                    <Tag color="error" style={{ margin: 0, fontSize: '11px', padding: '0 4px', flexShrink: 0 }}>Unavailable</Tag>
                )}
            </div>

            {isHealthy ? (
                <Row gutter={[8, 8]}>
                    {/* CPU Metric */}
                    <Col xs={24} sm={12}>
                        <div style={{
                            padding: '8px',
                            background: '#fafafa',
                            borderRadius: '6px',
                            border: `1px solid ${cpuUsagePercent > 80 ? '#ffccc7' : cpuUsagePercent > 60 ? '#ffe7ba' : '#e8e8e8'}`
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Zap size={12} style={{ color: cpuUsagePercent > 80 ? '#ff4d4f' : cpuUsagePercent > 60 ? '#fa8c16' : '#595959' }} />
                                    <Text strong style={{ fontSize: '11px', color: '#262626' }}>CPU</Text>
                                </div>
                                <Text strong style={{ 
                                    fontSize: '13px', 
                                    color: cpuUsagePercent > 80 ? '#ff4d4f' : cpuUsagePercent > 60 ? '#fa8c16' : '#262626',
                                    fontWeight: 600 
                                }}>
                                    {cpuUsagePercent}%
                                </Text>
                            </div>
                            <Progress
                                percent={cpuUsagePercent}
                                strokeColor={cpuUsagePercent > 80 ? '#ff4d4f' : cpuUsagePercent > 60 ? '#fa8c16' : '#52c41a'}
                                trailColor="#f0f0f0"
                                showInfo={false}
                                strokeWidth={4}
                                size="small"
                                style={{ marginBottom: '4px' }}
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text style={{ fontSize: '11px', color: '#262626', fontWeight: 500 }}>
                                    {cpuUsed} / {cpuAllocated} vCPU
                                </Text>
                                <Text type="secondary" style={{ fontSize: '11px', color: '#8c8c8c' }}>
                                    {cpuAvailable} free
                                </Text>
                            </div>
                        </div>
                    </Col>

                    {/* Memory Metric */}
                    <Col xs={24} sm={12}>
                        <div style={{
                            padding: '8px',
                            background: '#fafafa',
                            borderRadius: '6px',
                            border: `1px solid ${memoryUsagePercent > 80 ? '#ffccc7' : memoryUsagePercent > 60 ? '#ffe7ba' : '#e8e8e8'}`
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Database size={12} style={{ color: memoryUsagePercent > 80 ? '#ff4d4f' : memoryUsagePercent > 60 ? '#fa8c16' : '#595959' }} />
                                    <Text strong style={{ fontSize: '11px', color: '#262626' }}>Memory</Text>
                                </div>
                                <Text strong style={{ 
                                    fontSize: '13px', 
                                    color: memoryUsagePercent > 80 ? '#ff4d4f' : memoryUsagePercent > 60 ? '#fa8c16' : '#262626',
                                    fontWeight: 600 
                                }}>
                                    {memoryUsagePercent}%
                                </Text>
                            </div>
                            <Progress
                                percent={memoryUsagePercent}
                                strokeColor={memoryUsagePercent > 80 ? '#ff4d4f' : memoryUsagePercent > 60 ? '#fa8c16' : '#52c41a'}
                                trailColor="#f0f0f0"
                                showInfo={false}
                                strokeWidth={4}
                                size="small"
                                style={{ marginBottom: '4px' }}
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text style={{ fontSize: '11px', color: '#262626', fontWeight: 500 }}>
                                    {memoryUsed} / {memoryAllocated} GiB
                                </Text>
                                <Text type="secondary" style={{ fontSize: '11px', color: '#8c8c8c' }}>
                                    {memoryAvailable} free
                                </Text>
                            </div>
                        </div>
                    </Col>
                </Row>
            ) : (
                <div style={{
                    padding: '8px',
                    textAlign: 'center',
                    background: '#fffaf0',
                    borderRadius: '6px',
                    border: '1px solid #ffe7ba'
                }}>
                    <AlertTriangle size={14} style={{ color: '#faad14', marginBottom: '2px' }} />
                    <Text type="secondary" style={{ fontSize: '11px', display: 'block', color: '#8c8c8c' }}>
                        Resource metrics unavailable
                    </Text>
                </div>
            )}
        </Card>
    );
};

const KubernetesOverview = ({
    component
}: KubernetesOverviewProps) => {
    const { projectId, componentId } = useParams() as { projectId: string; envId: string; componentId: string; };
    const [loading, setLoading] = useState(true);
    const [clusterStatus, setClusterStatus] = useState<GetKubernetesStatusResponse>();
    const [offline, setOffline] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const loadData = async () => {
            try {
                const { data: clusterStatus } = await getKubeService().getKubernetesStatus(component.clusterId!, projectId);
                setClusterStatus(clusterStatus);
                setOffline(false);
            } catch (error) {
                if (
                    (error as any).response.data.kubernetesError?.code === 500 ||
                    (error as any).response.data.code === 404
                ) {
                    setOffline(true);
                }
            } finally {
                setLoading(false);
            }
        }

        const interval = setInterval(async () => {
            await loadData();
        }, 30000);

        loadData();

        return () => {
            clearInterval(interval);
        }
    }, [componentId, component.clusterId, projectId]);

    const isMobile = windowWidth < 768;

    if (offline) {
        return (
            <Card 
                bordered
                style={{
                    borderRadius: '8px',
                    border: '1px solid #e8e8e8',
                    background: '#ffffff'
                }}
            >
                <Empty
                    description={
                        <>
                            <div style={{ fontSize: '16px', marginBottom: '8px', fontWeight: 500, color: '#262626' }}>
                                Your Kubernetes cluster is currently offline.
                            </div>
                            <div style={{ fontSize: '14px', color: '#8c8c8c' }}>
                                If the cluster is still active in the cloud, you can&nbsp;
                                <a href="https://docs.microtica.com/eks-administration#Y8y55" target="_blank" style={{ fontWeight: 500 }}>
                                    connect
                                </a>
                                &nbsp;to it and check the status there.
                            </div>
                        </>
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            </Card>
        );
    }

    if (loading || !clusterStatus) {
        return <Skeleton active paragraph={{ rows: 8 }} />;
    }

    const nodes = clusterStatus.nodesStatus?.nodes || [];
    const version = nodes.length > 0 ? nodes[0].version : undefined;

    return (
        <div>
            {/* Cluster Header with Stats */}
            <ClusterHeader
                clusterId={clusterStatus.id}
                version={version}
                associatedNamespace={clusterStatus.associatedNamespace}
                nodes={nodes}
            />

            {/* Nodes Section */}
            <div>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px'
                }}>
                    <div style={{
                        width: '3px',
                        height: '14px',
                        background: '#262626',
                        borderRadius: '2px'
                    }} />
                    <Text strong style={{ fontSize: '14px', color: '#262626' }}>Nodes</Text>
                    <Badge 
                        count={nodes.length} 
                        style={{ 
                            backgroundColor: '#595959',
                            fontSize: '11px',
                            minWidth: '16px',
                            height: '16px',
                            lineHeight: '16px'
                        }}
                    />
                </div>

                {nodes.length === 0 ? (
                    <Card 
                        bordered
                        style={{
                            borderRadius: '8px',
                            background: '#fafafa',
                            border: '1px dashed #d9d9d9'
                        }}
                    >
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <>
                                    <div style={{ fontSize: '15px', marginBottom: '8px', fontWeight: 500, color: '#262626' }}>
                                        No active Kubernetes nodes found.
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#8c8c8c' }}>
                                        This may occur if the cluster is not yet set up, has encountered an error, or if there are currently no nodes available for deployment.
                                    </div>
                                </>
                            }
                        />
                    </Card>
                ) : (
                    <div style={{
                        maxHeight: isMobile ? 'none' : 'calc(100vh - 450px)',
                        overflowY: isMobile ? 'visible' : 'auto',
                        overflowX: 'hidden',
                        paddingRight: '4px'
                    }}>
                        {nodes
                            .slice()
                            .sort((a, b) => {
                                // Calculate usage percentages
                                const aIsHealthy = a.resources.status === "OK";
                                const bIsHealthy = b.resources.status === "OK";
                                
                                const aCpuPercent = aIsHealthy 
                                    ? Math.round((a.resources.usage.cpu / a.resources.allocated.cpu) * 100)
                                    : 0;
                                const aMemPercent = aIsHealthy
                                    ? Math.round((a.resources.usage.memory / a.resources.allocated.memory) * 100)
                                    : 0;
                                const aIsHighUsage = aCpuPercent > 80 || aMemPercent > 80;
                                
                                const bCpuPercent = bIsHealthy 
                                    ? Math.round((b.resources.usage.cpu / b.resources.allocated.cpu) * 100)
                                    : 0;
                                const bMemPercent = bIsHealthy
                                    ? Math.round((b.resources.usage.memory / b.resources.allocated.memory) * 100)
                                    : 0;
                                const bIsHighUsage = bCpuPercent > 80 || bMemPercent > 80;
                                
                                // High usage nodes first
                                if (aIsHighUsage && !bIsHighUsage) return -1;
                                if (!aIsHighUsage && bIsHighUsage) return 1;
                                
                                // If both are high usage or both are not, sort by average usage (descending)
                                const aAvgUsage = (aCpuPercent + aMemPercent) / 2;
                                const bAvgUsage = (bCpuPercent + bMemPercent) / 2;
                                return bAvgUsage - aAvgUsage;
                            })
                            .map((node, index, sortedNodes) => (
                                <NodeCard
                                    key={node.name || index}
                                    node={node}
                                    index={index}
                                    isMobile={isMobile}
                                    isLast={index === sortedNodes.length - 1}
                                />
                            ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default KubernetesOverview;
